import React, { useMemo } from 'react';
import { DataGrid } from '@v2/components/ui/DataGrid/DataGrid';
import { DateCellRenderer } from '@v2/components/ui/DataGrid/renderers/DateCellRenderer';
import { CreativeNameCellRenderer } from './renderers/CreativeNameCellRenderer';
import { CreativeStatusCellRenderer } from '@components/containers/CreativeIndexPage/renderers/CreativeStatusCellRenderer';
import { Scopes } from '@constants/scopes';
import { usePermissions } from '@components/hooks/permissions';
import { CreativeQCAlerts } from '@components/CreativeQCAlerts';
import { CreativeAdGroupCellRenderer } from './renderers/CreativeAdGroupCellRenderer';
import { GridColDef } from '@mui/x-data-grid-pro';
import { CreativeActionMenuCellRenderer } from '@components/Creatives/renderers/CreativeActionMenuCellRenderer';
import {
  CreativesTableContext,
  CreativesTableContextProvider,
} from '@components/Creatives/CreativesTableContext';
import { Box, Stack } from '@mui/material';
import { DataGridFilter } from '@v2/components/ui/DataGrid/DataGridFilter';
import { FileSizeCellRenderer } from '@v2/components/ui/DataGrid/renderers/FileSizeCellRenderer';
import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk';
import { DefaultCreativeStatusKeys, CreativeStatusDisplayNames } from '@constants';
import { StatusSelect } from '@components/StatusSelect';

export const CreativesTableV2 = () => {
  const { hasPermission } = usePermissions();
  const showQCFunctionality = hasPermission([Scopes.CAN_VIEW_QC]);
  const { hcmReleaseFilterCreativeLibraryByStatus } = useLDFlags();

  const columns = useMemo(
    () =>
      [
        {
          field: 'id',
          headerName: 'ID',
          width: 60,
          resizable: false,
          hideable: false,
        },
        {
          field: 'name',
          headerName: 'Name',
          renderCell: CreativeNameCellRenderer,
          minWidth: 300,
          hideable: false,
          sortable: true,
        },
        {
          field: 'status',
          headerName: 'Status',
          renderCell: CreativeStatusCellRenderer,
          minWidth: 180,
          hideable: false,
          sortable: false,
        },
        {
          field: 'action',
          headerName: '',
          width: 50,
          renderCell: CreativeActionMenuCellRenderer,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
        },
        {
          field: 'adGroup',
          headerName: 'Ad Group',
          flex: 1,
          renderCell: CreativeAdGroupCellRenderer,
          resizable: false,
          hideable: false,
          sortable: false,
        },
        {
          field: 'resolution',
          headerName: 'Resolution',
          minWidth: 90,
        },
        {
          field: 'file_size_bytes',
          headerName: 'File Size',
          minWidth: 90,
          align: 'right',
          renderCell: FileSizeCellRenderer,
        },
        {
          field: 'media_upload_date',
          headerName: 'Uploaded',
          renderCell: DateCellRenderer,
          minWidth: 150,
          resizable: false,
          hideable: false,
          sortable: false,
        },
      ].filter(Boolean) as GridColDef[],
    []
  );

  return (
    <CreativesTableContextProvider>
      <CreativesTableContext.Consumer>
        {({
          count,
          creatives,
          isLoading,
          setSize,
          hasMore,
          filter,
          setFilter,
          statuses,
          setStatuses,
        }) => {
          return (
            <>
              {showQCFunctionality && (
                <CreativeQCAlerts
                  email={'support@tvscientific.com'}
                  items={creatives || []}
                />
              )}
              {hcmReleaseFilterCreativeLibraryByStatus && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <StatusSelect
                    regularStatuses={DefaultCreativeStatusKeys}
                    statusDisplayNames={CreativeStatusDisplayNames}
                    chosenStatuses={statuses}
                    onChange={setStatuses}
                  />
                </Box>
              )}
              <Stack sx={{ height: '60vh' }}>
                <DataGridFilter
                  filter={filter}
                  onFilterChange={setFilter}
                  placeholder="Search creatives..."
                />
                <DataGrid
                  hasHeader
                  onRowsScrollEnd={() => {
                    if (hasMore && !isLoading) {
                      setSize(size => size + 1);
                    }
                  }}
                  initialState={{
                    pinnedColumns: {
                      left: ['id', 'name', 'status', 'action'],
                    },
                  }}
                  scrollEndThreshold={200}
                  loading={isLoading}
                  rows={creatives}
                  rowCount={count}
                  columns={columns}
                  disableRowSelectionOnClick
                />
              </Stack>
            </>
          );
        }}
      </CreativesTableContext.Consumer>
    </CreativesTableContextProvider>
  );
};
